import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Edit } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../utils/axios';
import { API_URL } from '../config/api.config';

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, typeFilter]);

  // Auto-detect customer type based on name
  const detectCustomerType = (name) => {
    const commercialIndicators = [
      'private limited', 'pvt ltd', 'pvt. ltd', 'ltd', 'limited',
      'inc', 'incorporated', 'llc', 'llp', 'corporation', 'corp',
      'company', 'co.', 'enterprises', 'solutions', 'technologies',
      'tech', 'systems', 'services', 'industries', 'group'
    ];

    const nameLower = name.toLowerCase();
    const isCommercial = commercialIndicators.some(indicator =>
      nameLower.includes(indicator)
    );

    return isCommercial ? 'Commercial' : 'Residential';
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/customers`);

      // Transform customer data
      const transformedCustomers = response.data.map(customer => {
        const customerName = customer.name || 'Unknown Customer';
        const detectedType = detectCustomerType(customerName);

        return {
          id: customer.id || customer._id,
          name: customerName,
          email: customer.email || 'N/A',
          phone: customer.phone || 'N/A',
          type: customer.customerType || customer.type || detectedType,
          address: customer.address || '',
          gstNumber: customer.gstNumber || ''
        };
      });

      setCustomers(transformedCustomers);
      console.log('📋 Loaded customers:', transformedCustomers.length);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (typeFilter !== 'All') {
      filtered = filtered.filter(customer => customer.type === typeFilter);
    }

    setFilteredCustomers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleExport = () => {
    try {
      // Prepare CSV data
      const csvHeaders = ['Name', 'Email', 'Phone', 'Type', 'Address', 'GST Number'];
      const csvRows = filteredCustomers.map(customer => [
        customer.name,
        customer.email,
        customer.phone,
        customer.type,
        customer.address || 'N/A',
        customer.gstNumber || 'N/A'
      ]);

      // Create CSV content
      let csvContent = csvHeaders.join(',') + '\n';
      csvRows.forEach(row => {
        csvContent += row.map(field => `"${field}"`).join(',') + '\n';
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const filterSuffix = typeFilter !== 'All' ? `_${typeFilter}` : '';
      const filename = `customers${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${filteredCustomers.length} customers`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export customers');
    }
  };

  const handleCreateEstimate = (customer) => {
    navigate('/sales/estimates?tab=form', {
      state: {
        prefilledCustomer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
          gstNumber: customer.gstNumber || ''
        }
      }
    });
  };

  const handleCreateInvoice = (customer) => {
    navigate('/sales/invoices?tab=form', {
      state: {
        prefilledCustomer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
          gstNumber: customer.gstNumber || ''
        }
      }
    });
  };

  return (
    <div className="customers-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .customers-page {
          min-height: 100vh;
          background-color: #f8fafc;
          padding: 24px;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* Header Section */
        .page-header {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
        }

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .page-title {
          font-size: 24px;
          font-weight: 600;
          color: #0A5F38;
          margin: 0;
        }

        .page-subtitle {
          font-size: 14px;
          color: #64748b;
          margin-top: 4px;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          gap: 8px;
        }

        .btn-primary {
          background-color: #0A5F38;
          color: white;
        }

        .btn-primary:hover {
          background-color: #084c2e;
        }

        .btn-secondary {
          background-color: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .btn-secondary:hover {
          background-color: #e2e8f0;
        }

        /* Search and Controls */
        .controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .search-box {
          position: relative;
          flex: 1;
          min-width: 280px;
          max-width: 400px;
        }

        .search-box input {
          width: 100%;
          padding: 10px 16px 10px 40px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background-color: white;
          font-size: 14px;
        }

        .search-box input:focus {
          outline: none;
          border-color: #C5A57D;
          box-shadow: 0 0 0 3px rgba(197, 165, 125, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .type-filter {
          position: relative;
          min-width: 160px;
        }

        .type-filter select {
          width: 100%;
          padding: 10px 16px;
          border-radius: 6px;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 36px;
        }
        .type-filter select:focus {
          outline: none;
          border-color: #C5A57D;
          box-shadow: 0 0 0 3px rgba(197, 165, 125, 0.1);
        }

        /* Table Styles */
        .table-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .table-header {
          display: grid;
          grid-template-columns: 2fr 2fr 1.5fr 1fr 1.5fr;
          padding: 16px 24px;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-weight: 600;
          color: #475569;
          font-size: 14px;
        }

        .table-row {
          display: grid;
          grid-template-columns: 2fr 2fr 1.5fr 1fr 1.5fr;
          padding: 16px 24px;
          border-bottom: 1px solid #f1f5f9;
          align-items: center;
          transition: background-color 0.2s;
        }

        .table-row:hover {
          background-color: #f8fafc;
        }

        .table-row:last-child {
          border-bottom: none;
        }

        /* Badge Styles */
        .badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .badge-purple {
          background-color: #f3e8ff;
          color: #7e22ce;
        }

        .badge-green {
          background-color: #dcfce7;
          color: #166534;
        }

        /* Action Buttons */
        .actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .action-btn {
          background: none;
          border: none;
          color: #0A5F38;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          padding: 4px 0;
          transition: color 0.2s;
        }

        .action-btn:hover {
          color: #084c2e;
        }

        .edit-btn {
          color: #64748b;
          padding: 6px;
          border-radius: 4px;
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .edit-btn:hover {
          background-color: #f1f5f9;
          color: #475569;
        }

        /* Loading and Empty States */
        .loading-state {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          color: #64748b;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #64748b;
        }

        /* Pagination Styles */
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 24px;
          padding: 20px 0;
        }

        .pagination-info {
          font-size: 14px;
          color: #64748b;
          margin-right: 16px;
        }

        .pagination-button {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 36px;
          height: 36px;
          padding: 0 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: white;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pagination-button:hover:not(:disabled) {
          border-color: #0A5F38;
          color: #0A5F38;
          background: #E6F0EB;
        }

        .pagination-button.active {
          background: #0A5F38;
          color: white;
          border-color: #0A5F38;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-ellipsis {
          padding: 0 8px;
          color: #64748b;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .customers-page {
            padding: 16px;
          }
          
          .header-top {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .btn-primary {
            width: 100%;
          }
          
          .controls {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-box {
            min-width: 100%;
            max-width: 100%;
          }
          
          .table-header {
            display: none;
          }
          
          .table-row {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 20px;
            gap: 12px;
          }
          
          .table-row > div {
            width: 100%;
            display: flex;
            justify-content: space-between;
          }
          
          .table-row > div::before {
            content: attr(data-label);
            font-weight: 600;
            color: #64748b;
          }
          
          .actions {
            width: 100%;
            justify-content: flex-end;
            margin-top: 8px;
          }

          .pagination-container {
            flex-wrap: wrap;
          }

          .pagination-info {
            width: 100%;
            text-align: center;
            margin-bottom: 12px;
          }
        }

        @media (max-width: 480px) {
          .page-title {
            font-size: 20px;
          }
          
          .actions {
            flex-wrap: wrap;
            justify-content: center;
          }
        }

        /* Tablet Responsive */
        @media (min-width: 769px) and (max-width: 1024px) {
          .table-header,
          .table-row {
            grid-template-columns: 1.5fr 1.5fr 1.2fr 0.8fr 1.2fr;
          }
        }

        /* ============================================
           RESPONSIVE DESIGN - MOBILE FIRST
           ============================================ */
        
        @media (max-width: 768px) {
          .customers-page {
            padding: 15px;
          }

          /* Header Section */
          .header-top {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .page-title {
            font-size: 20px;
          }

          .page-subtitle {
            font-size: 13px;
          }

          .btn-primary {
            width: 100%;
            justify-content: center;
          }

          /* Search and Filter Section */
          .search-filter-section {
            flex-direction: column;
            gap: 12px;
          }

          .search-container {
            width: 100% !important;
          }

          .filter-export-group {
            flex-direction: column;
            width: 100%;
            gap: 10px;
          }

          .filter-dropdown,
          .btn-export {
            width: 100%;
          }

          /* Customer Grid - Convert to Cards */
          .customer-grid {
            display: block;
          }

          .grid-header {
            display: none; /* Hide table header on mobile */
          }

          .grid-row {
            display: flex;
            flex-direction: column;
            padding: 15px;
            gap: 10px;
            margin-bottom: 12px;
            border-radius: 8px;
            background: white;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .grid-row > div {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            border-bottom: 1px solid #f1f5f9;
          }

          .grid-row > div:last-child {
            border-bottom: none;
          }

          .grid-row > div::before {
            content: attr(data-label);
            font-weight: 600;
            color: #64748b;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .grid-row > div:nth-child(1)::before {
            content: 'NAME';
          }

          .grid-row > div:nth-child(2)::before {
            content: 'EMAIL';
          }

          .grid-row > div:nth-child(3)::before {
            content: 'PHONE';
          }

          .grid-row > div:nth-child(4)::before {
            content: 'TYPE';
          }

          .grid-row > div:nth-child(5)::before {
            content: 'ACTIONS';
          }

          /* Actions on mobile */
          .actions-cell {
            flex-direction: column;
            gap: 8px;
            width: 100%;
          }

          .action-button {
            width: 100%;
            justify-content: center;
            padding: 10px;
          }

          /* Badges */
          .badge {
            font-size: 11px;
            padding: 4px 10px;
          }

          /* Pagination */
          .pagination {
            flex-wrap: wrap;
            gap: 5px;
          }

          .pagination-button {
            min-width: 35px;
            height: 35px;
            font-size: 13px;
          }

          .pagination-info {
            font-size: 12px;
            width: 100%;
            text-align: center;
            order: -1;
            margin-bottom: 10px;
          }

          /* Empty State */
          .empty-state h3 {
            font-size: 18px;
          }

          .empty-state p {
            font-size: 13px;
          }
        }

        @media (max-width: 480px) {
          .customers-page {
            padding: 10px;
          }

          .page-title {
            font-size: 18px;
          }

          .btn {
            font-size: 13px;
            padding: 8px 12px;
          }

          .grid-row {
            padding: 12px;
          }

          .grid-row > div {
            font-size: 13px;
          }

          .grid-row > div::before {
            font-size: 11px;
          }
        }
      `}</style>

      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-top">
            <div>
              <h1 className="page-title">CUSTOMERS</h1>
              <p className="page-subtitle">Manage your customer database</p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="controls">
          <div className="search-box">
            <Search className="search-icon" size={16} />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="type-filter">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="All">All Customers</option>
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
            </select>
          </div>

          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>

        {/* Table */}
        <div className="table-container">
          <div className="table-header">
            <div>NAME</div>
            <div>EMAIL</div>
            <div>PHONE</div>
            <div>TYPE</div>
            <div>ACTIONS</div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div>Loading customers...</div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="empty-state">
              <h3>No customers found</h3>
              <p>Try adjusting your search</p>
            </div>
          ) : (
            currentCustomers.map(customer => (
              <div key={customer.id} className="table-row">
                <div data-label="NAME">{customer.name}</div>
                <div data-label="EMAIL">{customer.email}</div>
                <div data-label="PHONE">{customer.phone}</div>
                <div data-label="TYPE">
                  <span className={`badge ${customer.type === 'Commercial' ? 'badge-purple' : 'badge-green'}`}>
                    {customer.type}
                  </span>
                </div>
                <div className="actions">
                  <button className="action-btn" onClick={() => handleCreateEstimate(customer)}>
                    Estimate
                  </button>
                  <button className="action-btn" onClick={() => handleCreateInvoice(customer)}>
                    Invoice
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredCustomers.length > 0 && (
          <div className="pagination-container">
            <span className="pagination-info">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredCustomers.length)} of {filteredCustomers.length}
            </span>

            <button
              className="pagination-button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;

              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}

            <button
              className="pagination-button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
